'use client';
import { useReactFlow, useStore as useReactFlowStore } from '@xyflow/react';
import { useCallback, useEffect } from 'react';
import { useStore } from '@/app/providers/store';
import type { CanvasUINode } from '@/app/types';
import { CANVAS_FOCUS_OFFSET_X, CANVAS_FOCUS_OFFSET_Y } from '../constants';

/**
 * Canvas 控制 Actions
 * 使用模块级变量存储函数引用，避免触发状态更新
 */

// 使用单例模式存储 action，避免状态更新导致的渲染问题
let focusNodeByContentIdRef: ((contentId: string) => void) | null = null;
let focusNodeByIdRef: ((nodeId: string, options?: { zoom?: number; duration?: number }) => void) | null = null;

/**
 * 注册 Canvas 的 focusNodeByContentId 函数
 * 在 Canvas 组件中调用，注册定位功能
 */
export function registerCanvasFocusAction(action: ((contentId: string) => void) | null) {
  focusNodeByContentIdRef = action;
}

/**
 * 注册 Canvas 的 focusNode 函数（通过 nodeId 定位）
 * 在 Canvas 组件中调用，注册定位功能
 */
export function registerCanvasFocusNodeAction(
  action: ((nodeId: string, options?: { zoom?: number; duration?: number }) => void) | null,
) {
  focusNodeByIdRef = action;
}

/**
 * 获取 focusNodeByContentId 函数的 Hook
 * 在其他组件中使用，调用定位功能
 */
export function useFocusNodeByContentId() {
  return (contentId: string) => {
    if (focusNodeByContentIdRef) {
      focusNodeByContentIdRef(contentId);
    } else {
      console.warn('Canvas focus action not registered yet');
    }
  };
}

/**
 * 获取 focusNode 函数的 Hook（通过 nodeId 定位）
 * 在其他组件中使用，调用定位功能
 */
export function useFocusNodeById() {
  return (nodeId: string, options?: { zoom?: number; duration?: number }) => {
    if (focusNodeByIdRef) {
      focusNodeByIdRef(nodeId, options);
    } else {
      console.warn('Canvas focus action not registered yet');
    }
  };
}

/**
 * Canvas 整体控制 Hook
 * 提供节点选中、定位、缩放等操作
 */
export function useCanvasControl() {
  const { setCenter, getNode, setNodes, getNodes, zoomIn, zoomOut, getViewport } = useReactFlow();
  // 使用 useStore 获取 ReactFlow 容器的高度
  const flowHeight = useReactFlowStore((state) => state.height);
  const agentNodeBusinessDataMap = useStore((state) => state.agentNodeBusinessDataMap);

  /**
   * 通过 content_id 查找对应的节点
   * @param contentId content ID
   * @returns 找到的节点，如果没找到返回 undefined
   */
  const findNodeByContentId = useCallback(
    (contentId: string): CanvasUINode | undefined => {
      const nodes = getNodes() as CanvasUINode[];
      for (const node of nodes) {
        const businessDataIds = node.data.businessDataIds || [];
        for (const businessDataId of businessDataIds) {
          const businessData = agentNodeBusinessDataMap.get(businessDataId);
          if (businessData?.data.content_id === contentId) {
            return node;
          }
        }
      }
      return undefined;
    },
    [getNodes, agentNodeBusinessDataMap],
  );

  /**
   * 选中并定位到指定节点（通过 nodeId）
   * @param nodeId 节点 ID
   * @param options 配置选项
   */
  const focusNode = useCallback(
    (nodeId: string, options: { zoom?: number; duration?: number } = {}) => {
      const node = getNode(nodeId);
      if (!node) {
        return;
      }

      const { zoom = 1, duration = 500 } = options;

      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          selected: n.id === nodeId,
        })),
      );

      // X 轴：节点中心 + 固定偏移量（保持不变）
      const x = node.position.x + ((node.measured?.width as number) || 0) / 2 + CANVAS_FOCUS_OFFSET_X;

      // Y 轴：节点顶部 + 根据容器高度动态计算的偏移量
      const dynamicOffsetY = (flowHeight * 0.15) / zoom;
      const y = node.position.y + dynamicOffsetY + CANVAS_FOCUS_OFFSET_Y;

      setCenter(x, y, { zoom, duration });
    },
    [getNode, setCenter, setNodes, getViewport, flowHeight],
  );

  /**
   * 通过 content_id 选中并定位到对应的节点
   * @param contentId content ID
   * @param options 配置选项
   */
  const focusNodeByContentId = useCallback(
    (contentId: string, options: { zoom?: number; duration?: number } = {}) => {
      const node = findNodeByContentId(contentId);
      if (!node) {
        return;
      }

      // 直接调用 focusNode，复用逻辑
      focusNode(node.id, options);
    },
    [findNodeByContentId, focusNode],
  );

  /**
   * Canvas 缩放快捷键
   * 监听 Command/Ctrl + 和 Command/Ctrl - 快捷键来控制画板缩放
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // 检查是否按下 Command (Mac) 或 Ctrl (Windows/Linux)
      const isModifierPressed = event.metaKey || event.ctrlKey;

      if (!isModifierPressed) return;

      // Command/Ctrl + "=" 或 "+" 放大
      if (event.key === '=' || event.key === '+') {
        event.preventDefault();
        zoomIn({ duration: 200 });
      }
      // Command/Ctrl + "-" 缩小
      else if (event.key === '-') {
        event.preventDefault();
        zoomOut({ duration: 200 });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [zoomIn, zoomOut]);

  return {
    focusNode,
    focusNodeByContentId,
  };
}
